import React, { useMemo } from 'lib/react';
import { useSelector } from 'lib/redux';
import {
  EVGrid,
  EVTypography,
  makeStyles,
} from 'lib/components';
import clsx from 'lib/clsx';
import { useTranslation } from 'lib/translation';
import { trim } from 'lodash';
import { isEntitledForHidePricingSelector, isSubscriptionDashboardV2EnabledSelector } from 'utils/appSelectors.utils';
import Typography from 'commonComponents/typography';
import {
  getProductIdsBySubscriptionSkuName,
  PRIMARY_SUBSCRIPTION_PRODUCTS,
  normalizeSubscriptionProductLabelForMatch,
} from 'features/order/Order.constants';
import SubscriptionConsumptionBar from './SubscriptionConsumptionBar.component';

const useStyles = makeStyles((theme) => ({
  orderDetails: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '26px 0',
    '@media (min-width: 1024px)': {
      padding: '0',
    },
    height: '100%',
    rowGap: '26px',
  },
  orderDetailsListItem: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '26px',
    '&.borderBottom': {
      paddingBottom: '26px',
      borderBottom: `1px solid ${theme.evPalette.charcoal[400]}`,
    },
    '&>div': {
      flexWrap: 'nowrap',
      gap: '8px',
      flexDirection: 'column',
      '@media (min-width: 1024px)': {
        flexDirection: 'row',
      },
    },
    '&>div>p': {
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
      color: theme.evPalette.charcoal[900],
      display: 'flex',
      alignItems: 'center',
      '&.bold': { fontWeight: 600 },
      '&:first-child': {
        '@media (min-width: 1024px)': {
          minWidth: '40%',
        },
        fontWeight: 600,
        fontSize: '12px',
        lineHeight: '18px',
        textTransform: 'uppercase',
        color: theme.evPalette.charcoal[800],
      },
    },
  },
  estimatedCostValue: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    alignItems: 'flex-start',
    width: '100%',
    minWidth: 0,
    flex: 1,
  },
  unitBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    borderRadius: '9999px',
    backgroundColor: theme.evPalette.ocean[100],
    color: '#2274F7',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px',
  },
}));

function OrderDetails() {
  const {
    orderDetails,
    measurementRequestType,
    changesIn2Years,
    reportEmailCC,
    estimatedCost,
    isBillingAffiliateUser,
    bluePrintId,
    isPropertyDataOrder,
    isUpgradeOrder,
    mapPoints,
  } = useSelector(({
    orderSubmitted, userData: { initParameters },
  }) => ({
    ...orderSubmitted,
    // #genai optional chaining, flag is only read as a boolean below
    isBillingAffiliateUser: initParameters?.isBillingAffiliate,
    bluePrintId: orderSubmitted.bluePrintId,
    isUpgradeOrder: Boolean(orderSubmitted.isUpgradeOrder),
    mapPoints: orderSubmitted.mapPoints ?? [],
  }));
  const isEntitledForHidePricing = useSelector(isEntitledForHidePricingSelector);
  const isSubscriptionDashboardV2Enabled = useSelector(isSubscriptionDashboardV2EnabledSelector);
  const primarySubscriptionProductsWithUsage = useSelector(({ order }) => order.primarySubscriptionProductsWithUsage ?? []);
  const { t } = useTranslation();
  const classes = useStyles();
  const subscriptions = useSelector(({ userData }) => userData?.subscriptionDashboard?.subscriptions ?? []);
  const pinMultiplier = bluePrintId ? 1 : Math.max(Number(mapPoints?.length) || 0, 1);

  const usageByProductIdFromApi = useMemo(() => {
    const map = {};
    (subscriptions ?? []).forEach((s) => {
      const name = (s.subscriptionSkuName || '').trim();
      const ids = getProductIdsBySubscriptionSkuName(name);
      if (!ids.length) return;
      ids.forEach((id) => {
        map[id] = {
          usedQty: s.usedQty ?? 0,
          includedQty: s.includedQty ?? 0,
        };
      });
    });
    return map;
  }, [subscriptions]);

  // Single plan = no subscription name maps to multiple product IDs. In single plan every product is 1 unit.
  const isBundlePlanFromSubscriptions = useMemo(
    () => (subscriptions ?? []).some((s) => getProductIdsBySubscriptionSkuName((s.subscriptionSkuName || '').trim()).length > 1),
    [subscriptions],
  );

  // V2: subscription products from order reducer (merged dashboard + usage). Legacy: from userData.subscriptionDashboard.
  const subscriptionProductNames = useMemo(() => {
    if (isSubscriptionDashboardV2Enabled && primarySubscriptionProductsWithUsage?.length > 0) {
      return new Set(
        primarySubscriptionProductsWithUsage
          .filter((p) => p.isSubscription && (p.includedQty ?? 0) > 0)
          .map((p) => normalizeSubscriptionProductLabelForMatch(p.name)),
      );
    }
    if (!subscriptions?.length) return new Set();
    const set = new Set();
    PRIMARY_SUBSCRIPTION_PRODUCTS.forEach((p) => {
      const usage = usageByProductIdFromApi[p.productId];
      if (usage != null && (Number(usage.includedQty) || 0) > 0) set.add(normalizeSubscriptionProductLabelForMatch(p.name));
    });
    return set;
  }, [isSubscriptionDashboardV2Enabled, primarySubscriptionProductsWithUsage, subscriptions, usageByProductIdFromApi]);

  // Exact match so "Walls" (108) does not match "Walls, Windows, Doors" (107). Allow combined labels like "Full House and ESX".
  const isSubscriptionProduct = (productName) => {
    if (!productName) return false;
    const normalized = normalizeSubscriptionProductLabelForMatch(productName);
    if (subscriptionProductNames.has(normalized)) return true;
    return [...subscriptionProductNames].some(
      (name) => normalized.startsWith(`${name} and `),
    );
  };

  // Unit badge: V2 use order state (has bundle-adjusted unitBadgeCount); else infer from subscriptions. Single plan = 1 unit for all.
  const unitBadgeForProduct = (productName) => {
    if (!productName) return 1;
    // #genai A report upgrade always consumes 1 unit. Full House is 2 only on a new order.
    if (isUpgradeOrder) return 1;
    const normalizedProduct = normalizeSubscriptionProductLabelForMatch(productName);
    if (isSubscriptionDashboardV2Enabled && primarySubscriptionProductsWithUsage?.length > 0) {
      const fromOrder = primarySubscriptionProductsWithUsage.find(
        (p) => {
          const pName = normalizeSubscriptionProductLabelForMatch(p.name ?? '');
          return normalizedProduct === pName || normalizedProduct.startsWith(`${pName} and `);
        },
      );
      // #genai optional chaining covers the previous null/undefined guard on fromOrder
      if (fromOrder?.unitBadgeCount != null) {
        const baseUnits = Number(fromOrder.unitBadgeCount) || 1;
        return baseUnits * pinMultiplier;
      }
    }
    const productFromConstants = PRIMARY_SUBSCRIPTION_PRODUCTS.find(
      (p) => {
        const pName = normalizeSubscriptionProductLabelForMatch(p.name ?? '');
        return normalizedProduct === pName || normalizedProduct.startsWith(`${pName} and `);
      },
    );
    // Single plan: every product is 1 unit. Bundle plan: use constant (e.g. Full House = 2).
    const baseUnits = !isBundlePlanFromSubscriptions ? 1 : (productFromConstants?.unitBadgeCount ?? 1);
    return (Number(baseUnits) || 1) * pinMultiplier;
  };

  const getPrimarySubscriptionProductIdFromName = (productName) => {
    if (!productName) return null;
    const normalized = normalizeSubscriptionProductLabelForMatch(productName);
    const fromConstants = PRIMARY_SUBSCRIPTION_PRODUCTS.find((p) => {
      const pName = normalizeSubscriptionProductLabelForMatch(p.name ?? '');
      return normalized === pName || normalized.startsWith(`${pName} and `);
    });
    return fromConstants?.productId ?? null;
  };

  const isOverageForSubmittedProduct = (productName, unitsOrdered = 1) => {
    const productId = getPrimarySubscriptionProductIdFromName(productName);
    if (!productId) return false;
    const usage = usageByProductIdFromApi[productId];
    const total = Number(usage?.includedQty) || 0;
    const usedBefore = Number(usage?.usedQty) || 0;
    const units = Number(unitsOrdered) || 1;
    if (total <= 0) return false;
    return (usedBefore + units) > total;
  };
  return (
    <EVGrid item className={classes.orderDetails}>
      {orderDetails?.map(({
        product,
        delivery,
        deliverySubtitle,
      }) => {
        const unitsOrdered = unitBadgeForProduct(product);
        const shouldShowUsage = isSubscriptionDashboardV2Enabled
          && isSubscriptionProduct(product)
          && !isOverageForSubmittedProduct(product, unitsOrdered);
        return (
          <EVGrid className={clsx(classes.orderDetailsListItem, orderDetails.length > 1 && 'borderBottom')} key={product}>
            <EVGrid container>
              <EVTypography>{t('order_submitted__product_type')}</EVTypography>
              <EVTypography>{product}</EVTypography>
            </EVGrid>
            <EVGrid container>
              <EVTypography>{t('order_submitted__delivery_method')}</EVTypography>
              <EVGrid>
                <EVTypography>{delivery}</EVTypography>
                {deliverySubtitle && <Typography variant="subtitle">{deliverySubtitle}</Typography>}
              </EVGrid>
            </EVGrid>
            {(bluePrintId === '')
              && (
                <>
                  <EVGrid container>
                    <EVTypography>{t('order_submitted__structure')}</EVTypography>
                    <EVTypography>{isPropertyDataOrder ? 'Other (please provide instructions)' : measurementRequestType}</EVTypography>
                  </EVGrid>
                  {!isPropertyDataOrder && (
                    <EVGrid container>
                      <EVTypography>{t('order_submitted__changes')}</EVTypography>
                      <EVTypography>{changesIn2Years}</EVTypography>
                    </EVGrid>
                  )}
                </>
              )}
            <EVGrid container>
              <EVTypography>{t('order_submitted__additional_recipients')}</EVTypography>
              <EVGrid container direction="column" style={{ width: 'unset' }}>
                {
                  reportEmailCC?.replace('Send Report to Additional Email Addresses: ', '').split(';').map(trim).filter(Boolean)
                    .map((email) => <EVTypography key={email}>{email}</EVTypography>)
                }
              </EVGrid>
            </EVGrid>
            {!isBillingAffiliateUser && !isEntitledForHidePricing && (
            <EVGrid container>
              <EVTypography>{t('order_submitted__estimated_cost')}</EVTypography>
              <EVTypography className="bold">{estimatedCost}</EVTypography>
            </EVGrid>
            )}
            {!isBillingAffiliateUser && !isEntitledForHidePricing && shouldShowUsage && (
            <EVGrid container>
              <EVTypography>{t('order_submitted__usage')}</EVTypography>
              <EVGrid className={classes.estimatedCostValue}>
                <span className={classes.unitBadge}>
                  {unitsOrdered}
                  {' '}
                  Unit
                  {unitsOrdered !== 1 ? 's' : ''}
                </span>
                <SubscriptionConsumptionBar productName={product} unitsOrdered={unitsOrdered} />
              </EVGrid>
            </EVGrid>
            )}
          </EVGrid>
        );
      })}
    </EVGrid>
  );
}

export default OrderDetails;
